import type { PackageItemFormData } from "@src/common/components/PackageDimensionsForm/PackageDimensionsForm";
import { countPackageItemPieces, filterValidPackageItems } from "./packageItems";

export const PACKAGE_ITEM_COUNT_ERROR =
  "Package count cannot be less than package item piece count";

/** Row-level minimum, enforced on the quantity input itself. */
export const PACKAGE_ITEM_QUANTITY_ERROR = "Must be at least 1";

/** A quantity counts physical pieces, so fractions have no meaning. */
export const PACKAGE_ITEM_QUANTITY_WHOLE_NUMBER_ERROR = "Must be a whole number";

/**
 * The row-level quantity rule, in one place so the input's own validation and the plain-state
 * save gates cannot drift apart. Returns the message to show, or undefined when acceptable.
 *
 * The whole-number check comes first because `2.5` is not a malformed count so much as not a
 * count at all — and `<input type="number">` accepts both fractions and scientific notation
 * (`2e2`), so a value that never passed through an integer parse can reach here.
 */
export function validatePackageItemQuantity(
  quantity: number | null | undefined,
): string | undefined {
  if (quantity == null) return undefined;
  if (!Number.isInteger(quantity)) {
    return PACKAGE_ITEM_QUANTITY_WHOLE_NUMBER_ERROR;
  }
  if (quantity < 1) return PACKAGE_ITEM_QUANTITY_ERROR;
  return undefined;
}

interface ValidatePackageItemCountArgs {
  numPackages: number | null | undefined;
  packageItems: PackageItemFormData[] | null | undefined;
}

/**
 * Mirrors the backend `PackageItemCountValidator`: a row stands for its quantity in pieces, so
 * the package count is compared against the summed quantities rather than the row count — one
 * row of quantity 5 can overflow a count of 3 on its own.
 */
export function validatePackageItemCount({
  numPackages,
  packageItems,
}: ValidatePackageItemCountArgs): string | undefined {
  if (!packageItems) return undefined;
  if (numPackages == null) return undefined;
  const persistedItems = filterValidPackageItems(packageItems) ?? [];
  if (numPackages < countPackageItemPieces(persistedItems)) {
    return PACKAGE_ITEM_COUNT_ERROR;
  }
  return undefined;
}

/**
 * Whether any row carries a quantity the backend would reject. Forms driven by react-hook-form
 * get this from the quantity field's own `validate` rule; plain-state forms (quote edit) call
 * this to gate their save button.
 */
export function hasInvalidPackageItemQuantity(
  packageItems: { quantity?: number | null }[] | null | undefined,
): boolean {
  return (packageItems ?? []).some(
    (item) => validatePackageItemQuantity(item.quantity) != null,
  );
}
